import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getAllHomes, purchaseHome } from "../../services/firebase/homes";
import { useSelector } from "react-redux";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  getDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase/config";

function Marketplace() {
  // State for tab selection
  const [activeTab, setActiveTab] = useState("homes");

  // Homes state
  const [homes, setHomes] = useState([]);
  const [loadingHomes, setLoadingHomes] = useState(true);
  const [errorHomes, setErrorHomes] = useState(null);

  // Items state
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);

  // Detail view state
  const [selectedHome, setSelectedHome] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Common state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const user = useSelector((state) => state.auth.user);

  // Fetch homes
  useEffect(() => {
    const fetchHomes = async () => {
      try {
        const homesData = await getAllHomes();
        setHomes(homesData);
      } catch (err) {
        setErrorHomes("Failed to fetch homes");
        console.error("Error fetching homes:", err);
      } finally {
        setLoadingHomes(false);
      }
    };

    fetchHomes();
  }, []);

  // Fetch items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const itemsSnapshot = await getDocs(collection(db, "items"));
        const itemsData = itemsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setItems(itemsData);
      } catch (error) {
        console.error("Error fetching items:", error);
        setSnackbar({
          open: true,
          message: "Failed to load items",
          severity: "error",
        });
      } finally {
        setLoadingItems(false);
      }
    };

    fetchItems();
  }, []);

  // Home purchase handler
  const handleHomePurchase = async (homeId) => {
    try {
      const result = await purchaseHome(homeId, user.uid);
      setSnackbar({
        open: true,
        message: `Successfully purchased ${result.address}! Your new balance is ${result.newBalance} ND`,
        severity: "success",
      });
      // Refresh homes list
      const updatedHomes = await getAllHomes();
      setHomes(updatedHomes);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to purchase home",
        severity: "error",
      });
    }
  };

  // Item purchase handler
  const handleItemPurchase = async (item) => {
    try {
      // Check if user has enough neighborhood dollars
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.data();

      if (userData.neighborhoodDollars < item.price) {
        setSnackbar({
          open: true,
          message: "Insufficient neighborhood dollars to make this purchase",
          severity: "error",
        });
        return;
      }

      // Check if item is still available
      if (item.quantity <= 0) {
        setSnackbar({
          open: true,
          message: "This item is out of stock",
          severity: "error",
        });
        return;
      }

      // Update item quantity and purchasedBy array
      await updateDoc(doc(db, "items", item.id), {
        quantity: item.quantity - 1,
        purchasedBy: arrayUnion(user.uid),
      });

      // Update user's items array and deduct neighborhood dollars
      await updateDoc(doc(db, "users", user.uid), {
        items: arrayUnion({
          id: item.id,
          name: item.name,
          image: item.image,
          category: item.category,
          location: item.location,
          purchasedAt: new Date(),
        }),
        neighborhoodDollars: userData.neighborhoodDollars - item.price,
      });

      setSnackbar({
        open: true,
        message: `Successfully purchased ${item.name}!`,
        severity: "success",
      });

      // Refresh items list
      const itemsSnapshot = await getDocs(collection(db, "items"));
      const itemsData = itemsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setItems(itemsData);
    } catch (error) {
      console.error("Error purchasing item:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to purchase item",
        severity: "error",
      });
    }
  };

  // Detail view handlers
  const handleOpenDetails = (item, type) => {
    if (type === "home") {
      setSelectedHome(item);
      setSelectedItem(null);
      setCurrentImageIndex(0); // Reset image index for new home
    } else {
      setSelectedItem(item);
      setSelectedHome(null);
    }
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Loading states
  if (
    (activeTab === "homes" && loadingHomes) ||
    (activeTab === "items" && loadingItems)
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state for homes
  if (activeTab === "homes" && errorHomes) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography color="error">{errorHomes}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, backgroundColor: "#f2f0e4" }}>
      {/* Tab buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 4,
          mb: 4,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setActiveTab("homes")}
          sx={{
            backgroundColor: activeTab === "homes" ? "#000000" : "#f2f0e4",
            color: activeTab === "homes" ? "#ffffff" : "#000000",
            border: "1px solid #000000",
            borderRadius: 0,
            px: 4,
            py: 1,
            "&:hover": {
              backgroundColor: activeTab === "homes" ? "#000000" : "#e6e4d9",
            },
          }}
        >
          HOMEs
        </Button>
      </Box>

      {/* Section title */}
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: "bold",
          textTransform: "uppercase",
        }}
      >
        {activeTab === "homes" ? "HOMEs" : "ITEMs"}
      </Typography>

      {/* Content */}
      {activeTab === "homes" && (
        <Grid container spacing={3}>
          {homes.map((home) => (
            <Grid item xs={6} sm={6} md={4} key={home.id}>
              <Box
                sx={{
                  border: "1px solid #000000",
                  borderRadius: 0,
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 3,
                  },
                }}
                onClick={() => handleOpenDetails(home, "home")}
              >
                <Box
                  sx={{
                    height: "auto",
                    backgroundColor: "#9e9e9e",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#000000",
                    border: "1px solid #000000",
                  }}
                >
                  {(home.images && home.images.length > 0) || home.image ? (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={
                          home.images && home.images.length > 0
                            ? home.images[0]
                            : home.image
                        }
                        alt={home.address}
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                      {home.images && home.images.length > 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0,0,0,0.7)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                          }}
                        >
                          +{home.images.length - 1} more
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography>photo</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f2f0e4",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    PRICE: {home.price} ND
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    {home.features &&
                      home.features.map((feature, index) => (
                        <Box
                          key={index}
                          sx={{
                            border: "1px solid #000000",
                            p: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "0.8rem",
                          }}
                        >
                          {feature}
                        </Box>
                      ))}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      mt: "auto",
                      mb: 2,
                    }}
                  >
                    {home.address}
                  </Typography>

                  <Button
                    sx={{
                      backgroundColor: "#000000",
                      color: "white",
                      borderRadius: 0,
                      "&:hover": {
                        backgroundColor: "#333333",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent opening dialog
                      handleOpenDetails(home, "home");
                    }}
                  >
                    VIEW DETAILS
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === "items" && (
        <Grid container spacing={3}>
          {items.map((item) => (
            <Grid item xs={6} sm={6} md={4} key={item.id}>
              <Box
                sx={{
                  border: "1px solid #000000",
                  borderRadius: 0,
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#f2f0e4",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 3,
                  },
                }}
                onClick={() => handleOpenDetails(item, "item")}
              >
                <Box
                  sx={{
                    height: "auto",
                    backgroundColor: "#9e9e9e",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#000000",
                  }}
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography>photo</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "#f2f0e4",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    ITEM : {item.name}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                    }}
                  >
                    PRICE : {item.price} ND
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <Button
                      sx={{
                        flex: 1,
                        backgroundColor: "#000000",
                        color: "white",
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: "#333333",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent opening dialog
                        handleOpenDetails(item, "item");
                      }}
                    >
                      VIEW
                    </Button>

                    <Button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent opening dialog
                        handleItemPurchase(item);
                      }}
                      sx={{
                        flex: 1,
                        backgroundColor: "#FF5722",
                        color: "white",
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: "#E64A19",
                        },
                      }}
                    >
                      BUY
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 1500 }}
      >
        {selectedHome && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f2f0e4",
                borderBottom: "1px solid #000000",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {selectedHome.address}
              </Typography>
              <IconButton onClick={handleCloseDetails} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: "#f2f0e4", p: 0 }}>
              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    mb: 3,
                    border: "1px solid #000000",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {selectedHome.images && selectedHome.images.length > 0 ? (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          minHeight: "300px",
                        }}
                      >
                        <img
                          src={
                            selectedHome.images[currentImageIndex] ||
                            selectedHome.images[0]
                          }
                          alt={`${selectedHome.address} - Image ${
                            currentImageIndex + 1
                          }`}
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "60vh",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                      {selectedHome.images.length > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 2,
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <Button
                            onClick={() =>
                              setCurrentImageIndex((prev) =>
                                prev > 0
                                  ? prev - 1
                                  : selectedHome.images.length - 1
                              )
                            }
                            disabled={selectedHome.images.length <= 1}
                          >
                            Previous
                          </Button>
                          <Typography variant="body2">
                            {currentImageIndex + 1} of{" "}
                            {selectedHome.images.length}
                          </Typography>
                          <Button
                            onClick={() =>
                              setCurrentImageIndex((prev) =>
                                prev < selectedHome.images.length - 1
                                  ? prev + 1
                                  : 0
                              )
                            }
                            disabled={selectedHome.images.length <= 1}
                          >
                            Next
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        minHeight: "300px",
                      }}
                    >
                      <img
                        src={selectedHome.image}
                        alt={selectedHome.address}
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "60vh",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      DETAILS
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>PRICE:</strong> {selectedHome.price} Neighborhood
                      Dollars
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>STATUS:</strong> {selectedHome.status}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>OWNER:</strong>{" "}
                      {selectedHome.ownerId ? "Occupied" : "Available"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      FEATURES
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedHome.features &&
                        selectedHome.features.map((feature, index) => (
                          <Box
                            key={index}
                            sx={{
                              border: "1px solid #000000",
                              p: 1,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              fontSize: "0.8rem",
                            }}
                          >
                            {feature}
                          </Box>
                        ))}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      DESCRIPTION
                    </Typography>
                    <Typography variant="body1">
                      {selectedHome.description ||
                        "A beautiful home in the neighborhood."}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                backgroundColor: "#f2f0e4",
                borderTop: "1px solid #000000",
                p: 2,
              }}
            >
              {selectedHome.status === "available" && (
                <Button
                  onClick={() => {
                    handleHomePurchase(selectedHome.id);
                    handleCloseDetails();
                  }}
                  sx={{
                    backgroundColor: "#000000",
                    color: "white",
                    borderRadius: 0,
                    "&:hover": {
                      backgroundColor: "#333333",
                    },
                  }}
                >
                  PURCHASE HOME
                </Button>
              )}
              <Button
                onClick={handleCloseDetails}
                sx={{
                  border: "1px solid #000000",
                  borderRadius: 0,
                  color: "#000000",
                }}
              >
                CLOSE
              </Button>
            </DialogActions>
          </>
        )}

        {selectedItem && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f2f0e4",
                borderBottom: "1px solid #000000",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {selectedItem.name}
              </Typography>
              <IconButton onClick={handleCloseDetails} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: "#f2f0e4", p: 0 }}>
              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    justifyContent: "center",
                    border: "1px solid #000000",
                  }}
                >
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.name}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "60vh",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      DETAILS
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>PRICE:</strong> {selectedItem.price} Neighborhood
                      Dollars
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>CATEGORY:</strong>{" "}
                      {selectedItem.category || "General"}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>QUANTITY:</strong> {selectedItem.quantity || 0}{" "}
                      available
                    </Typography>
                    {selectedItem.location && (
                      <Typography variant="body1" gutterBottom>
                        <strong>LOCATION:</strong> {selectedItem.location}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      DESCRIPTION
                    </Typography>
                    <Typography variant="body1">
                      {selectedItem.description ||
                        "A useful item for your neighborhood life."}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                backgroundColor: "#f2f0e4",
                borderTop: "1px solid #000000",
                p: 2,
              }}
            >
              {selectedItem.quantity > 0 && (
                <Button
                  onClick={() => {
                    handleItemPurchase(selectedItem);
                    handleCloseDetails();
                  }}
                  sx={{
                    backgroundColor: "#FF5722",
                    color: "white",
                    borderRadius: 0,
                    "&:hover": {
                      backgroundColor: "#E64A19",
                    },
                  }}
                >
                  PURCHASE ITEM
                </Button>
              )}
              <Button
                onClick={handleCloseDetails}
                sx={{
                  border: "1px solid #000000",
                  borderRadius: 0,
                  color: "#000000",
                }}
              >
                CLOSE
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Marketplace;
